import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Users, Shield, UserCheck, Activity,
  AlertTriangle, QrCode, TrendingUp, ArrowRight, Loader2,
  Crown, Zap, Rocket, Clock, CheckCircle2, XCircle,
  BarChart3, Camera, CalendarCheck, UserX, Gauge, HeartPulse,
  Timer, FileWarning, Flame
} from 'lucide-react';
import { api } from '../../services/api';
import PlatformLayout from '../../components/layout/PlatformLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

export default function PlatformDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await api.getPlatformStats();
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PlatformLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-violet-500 mx-auto" />
            <p className="mt-3 text-slate-400">Loading platform statistics...</p>
          </div>
        </div>
      </PlatformLayout>
    );
  }

  if (error) {
    return (
      <PlatformLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Failed to Load</h2>
            <p className="text-slate-400 mb-4">{error}</p>
            <Button onClick={loadStats} className="bg-violet-600 hover:bg-violet-700">Try Again</Button>
          </div>
        </div>
      </PlatformLayout>
    );
  }

  const planIcons = { basic: Crown, pro: Zap, enterprise: Rocket };
  const planColors = {
    basic: 'from-slate-500 to-slate-600',
    pro: 'from-blue-500 to-blue-600',
    enterprise: 'from-violet-500 to-purple-600'
  };

  const usage = stats?.usage || {};
  const compliance = stats?.compliance || {};
  const health = stats?.system_health || {};

  return (
    <PlatformLayout>
      <div className="space-y-8" data-testid="platform-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white">Platform Overview</h1>
          <p className="text-slate-400 mt-1">Monitor all organizations and platform activity</p>
        </div>

        {/* Main Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 border-violet-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-violet-300">Total Organizations</p>
                  <p className="text-4xl font-bold text-white mt-1">{stats?.organizations?.total || 0}</p>
                  <p className="text-sm text-violet-300 mt-1">{stats?.organizations?.active || 0} active</p>
                </div>
                <div className="w-14 h-14 bg-violet-500/30 rounded-2xl flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-violet-300" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-300">Total Users</p>
                  <p className="text-4xl font-bold text-white mt-1">{stats?.users?.total || 0}</p>
                  <p className="text-sm text-blue-300 mt-1">across all orgs</p>
                </div>
                <div className="w-14 h-14 bg-blue-500/30 rounded-2xl flex items-center justify-center">
                  <Users className="w-7 h-7 text-blue-300" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-300">Guards</p>
                  <p className="text-4xl font-bold text-white mt-1">{stats?.users?.guards || 0}</p>
                  <p className="text-sm text-green-300 mt-1">active guards</p>
                </div>
                <div className="w-14 h-14 bg-green-500/30 rounded-2xl flex items-center justify-center">
                  <Shield className="w-7 h-7 text-green-300" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-300">Supervisors</p>
                  <p className="text-4xl font-bold text-white mt-1">{stats?.users?.supervisors || 0}</p>
                  <p className="text-sm text-amber-300 mt-1">+ {stats?.users?.admins || 0} admins</p>
                </div>
                <div className="w-14 h-14 bg-amber-500/30 rounded-2xl flex items-center justify-center">
                  <UserCheck className="w-7 h-7 text-amber-300" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Usage & Engagement + Compliance Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Usage & Engagement */}
          <Card className="bg-slate-800 border-slate-700" data-testid="usage-metrics">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-cyan-400" />
                Usage & Engagement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                  <CalendarCheck className="w-6 h-6 text-green-400 mx-auto mb-1.5" />
                  <p className="text-2xl font-bold text-white">{usage.total_shifts_completed || 0}</p>
                  <p className="text-xs text-slate-400">Shifts Completed</p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                  <QrCode className="w-6 h-6 text-blue-400 mx-auto mb-1.5" />
                  <p className="text-2xl font-bold text-white">{usage.total_scans || 0}</p>
                  <p className="text-xs text-slate-400">Total Scans</p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                  <Camera className="w-6 h-6 text-purple-400 mx-auto mb-1.5" />
                  <p className="text-2xl font-bold text-white">{usage.total_photos || 0}</p>
                  <p className="text-xs text-slate-400">Patrol Photos</p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                  <Flame className="w-6 h-6 text-orange-400 mx-auto mb-1.5" />
                  <p className="text-2xl font-bold text-white">{usage.active_guards_this_week || 0}</p>
                  <p className="text-xs text-slate-400">Active Guards (7d)</p>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-slate-700 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-300">Avg. Scans per Guard</span>
                  <span className="text-white font-semibold">{usage.avg_scans_per_guard || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-300">Scans This Month</span>
                  <span className="text-white font-semibold">{usage.scans_this_month || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-300">Shifts This Month</span>
                  <span className="text-white font-semibold">{usage.shifts_this_month || 0}</span>
                </div>
              </div>

              {usage.most_active_orgs?.length > 0 && (
                <div className="mt-5 pt-4 border-t border-slate-700">
                  <h4 className="text-sm font-medium text-slate-400 mb-3">Top Active Organizations</h4>
                  <div className="space-y-2">
                    {usage.most_active_orgs.map((org, i) => (
                      <div key={org.organization_id} className="flex items-center justify-between bg-slate-700/30 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-500 w-5">#{i + 1}</span>
                          <span className="text-sm text-white">{org.name}</span>
                        </div>
                        <span className="text-sm text-cyan-400 font-semibold">{org.scan_count} scans</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Compliance */}
          <Card className="bg-slate-800 border-slate-700" data-testid="compliance-metrics">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Gauge className="w-5 h-5 text-amber-400" />
                Compliance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                  <CheckCircle2 className="w-6 h-6 text-green-400 mx-auto mb-1.5" />
                  <p className="text-2xl font-bold text-white">{compliance.shift_compliance || 0}%</p>
                  <p className="text-xs text-slate-400">Shift Compliance</p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                  <Clock className="w-6 h-6 text-red-400 mx-auto mb-1.5" />
                  <p className="text-2xl font-bold text-white">{compliance.late_clockin_rate || 0}%</p>
                  <p className="text-xs text-slate-400">Late Clock-in Rate</p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                  <Timer className="w-6 h-6 text-blue-400 mx-auto mb-1.5" />
                  <p className="text-2xl font-bold text-white">{compliance.avg_resolution_hours || 0}h</p>
                  <p className="text-xs text-slate-400">Avg Resolution Time</p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                  <AlertTriangle className="w-6 h-6 text-amber-400 mx-auto mb-1.5" />
                  <p className="text-2xl font-bold text-white">{compliance.pending_approvals || 0}</p>
                  <p className="text-xs text-slate-400">Pending Approvals</p>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-slate-700 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-300">Incident Resolution Rate</span>
                  <span className={`font-semibold ${compliance.resolution_rate >= 80 ? 'text-green-400' : compliance.resolution_rate >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                    {compliance.resolution_rate || 0}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-300">Open Incidents</span>
                  <span className={`font-semibold ${compliance.open_incidents > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {compliance.open_incidents || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-300">Resolved Incidents</span>
                  <span className="text-green-400 font-semibold">{compliance.resolved_incidents || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-300">Late Clock-ins (Total)</span>
                  <span className="text-white font-semibold">{compliance.late_clockins_total || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-300">Total Attendance Records</span>
                  <span className="text-white font-semibold">{compliance.total_attendance || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* System Health + Plans Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* System Health */}
          <Card className="bg-slate-800 border-slate-700" data-testid="system-health">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <HeartPulse className="w-5 h-5 text-rose-400" />
                System Health
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Orgs Approaching Limits */}
              <div>
                <h4 className="text-sm font-medium text-slate-400 mb-2 flex items-center gap-1.5">
                  <FileWarning className="w-4 h-4" /> Organizations Near Plan Limit
                </h4>
                {health.orgs_near_limit?.length > 0 ? (
                  <div className="space-y-2">
                    {health.orgs_near_limit.map(org => (
                      <div key={org.organization_id} className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2.5">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-white font-medium">{org.name}</span>
                          <span className="text-xs text-amber-400 font-semibold">{org.usage_percent}% used</span>
                        </div>
                        <div className="mt-1.5 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${org.usage_percent >= 100 ? 'bg-red-500' : 'bg-amber-500'}`}
                            style={{ width: `${Math.min(org.usage_percent, 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{org.current_guards}/{org.guard_limit} guards ({org.plan} plan)</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-3 text-center">
                    <CheckCircle2 className="w-5 h-5 text-green-400 mx-auto mb-1" />
                    <p className="text-sm text-green-300">All organizations within limits</p>
                  </div>
                )}
              </div>

              {/* Dormant Orgs */}
              <div>
                <h4 className="text-sm font-medium text-slate-400 mb-2 flex items-center gap-1.5">
                  <UserX className="w-4 h-4" /> Dormant Organizations (30+ days)
                </h4>
                {health.dormant_orgs?.length > 0 ? (
                  <div className="space-y-2">
                    {health.dormant_orgs.map(org => (
                      <div key={org.organization_id} className="bg-slate-700/50 rounded-lg px-3 py-2.5 flex justify-between items-center">
                        <div>
                          <span className="text-sm text-white">{org.name}</span>
                          <span className="text-xs text-slate-500 ml-2">({org.plan})</span>
                        </div>
                        <span className="text-xs text-slate-400">Since {org.created_at ? new Date(org.created_at).toLocaleDateString() : 'N/A'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-3 text-center">
                    <CheckCircle2 className="w-5 h-5 text-green-400 mx-auto mb-1" />
                    <p className="text-sm text-green-300">No dormant organizations</p>
                  </div>
                )}
              </div>

              {/* Expiring Trials */}
              <div>
                <h4 className="text-sm font-medium text-slate-400 mb-2 flex items-center gap-1.5">
                  <Clock className="w-4 h-4" /> Expiring Trials (next 7 days)
                </h4>
                {health.expiring_trials?.length > 0 ? (
                  <div className="space-y-2">
                    {health.expiring_trials.map(org => (
                      <div key={org.id} className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5 flex justify-between items-center">
                        <span className="text-sm text-white">{org.name}</span>
                        <span className="text-xs text-red-400">Expires {org.trial_ends_at ? new Date(org.trial_ends_at).toLocaleDateString() : 'soon'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-3 text-center">
                    <CheckCircle2 className="w-5 h-5 text-green-400 mx-auto mb-1" />
                    <p className="text-sm text-green-300">No expiring trials</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Plans Breakdown + Today's Activity */}
          <div className="space-y-6">
            {/* Plans Breakdown */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-violet-400" />
                  Organizations by Plan
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(stats?.organizations?.by_plan || {}).map(([plan, count]) => {
                    const Icon = planIcons[plan] || Crown;
                    const gradient = planColors[plan] || 'from-slate-500 to-slate-600';
                    const total = stats?.organizations?.total || 1;
                    const percentage = Math.round((count / total) * 100) || 0;
                    return (
                      <div key={plan} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center`}>
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-medium text-white capitalize">{plan}</p>
                              <p className="text-sm text-slate-400">{count} organizations</p>
                            </div>
                          </div>
                          <span className="text-2xl font-bold text-white">{percentage}%</span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full bg-gradient-to-r ${gradient} rounded-full transition-all duration-500`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {Object.keys(stats?.organizations?.by_plan || {}).length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No organizations yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Today's Activity */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Activity className="w-5 h-5 text-green-400" />
                  Today's Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-700/50 rounded-xl p-5 text-center">
                    <QrCode className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-white">{stats?.activity_today?.scans || 0}</p>
                    <p className="text-sm text-slate-400">Checkpoint Scans</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-xl p-5 text-center">
                    <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-white">{stats?.activity_today?.incidents || 0}</p>
                    <p className="text-sm text-slate-400">Incidents Reported</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quick Actions */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <Link to="/platform/organizations">
                <Button className="bg-violet-600 hover:bg-violet-700">
                  <Building2 className="w-4 h-4 mr-2" />
                  Manage Organizations
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </PlatformLayout>
  );
}
